/**
 * Sentry bootstrap for the React bundle (browser + Electron renderer).
 *
 * Contract
 * ────────
 * - Imported as the very first statement of `src/main.tsx` so that the global
 *   error / unhandled-rejection handlers are installed before any app code
 *   runs (and before the React tree mounts).
 * - `init()` is a no-op when `VITE_SENTRY_DSN` is missing (default in local
 *   dev), keeping error noise off the browser console.
 * - Performance/session-replay features are not enabled in this phase. We only
 *   want error capture + source-map resolution against our build. Trace
 *   correlation with the server lives in Tempo; the server stamps `trace_id`
 *   onto its Sentry events (see server/observability/sentry.js).
 *
 * PII posture
 * ───────────
 * - `sendDefaultPii: false`.
 * - `beforeSend` strips any breadcrumb/event value whose key matches the
 *   sensitive-key pattern (authorization, token, password, …).
 * - `beforeBreadcrumb` drops `console` breadcrumbs entirely — the browser
 *   console often echoes auth tokens during dev and nothing we'd want to
 *   forward to Sentry.
 */

import * as Sentry from "@sentry/react";

const SENSITIVE_KEY = /(password|token|api[_-]?key|authorization|cookie|session|jwt|secret)/i;
const REDACTED = "[REDACTED]";

function redactInPlace(obj: unknown, seen = new WeakSet<object>(), depth = 0): unknown {
  if (!obj || typeof obj !== "object" || depth > 8) return obj;
  if (seen.has(obj as object)) return obj;
  seen.add(obj as object);

  if (Array.isArray(obj)) {
    for (const item of obj) redactInPlace(item, seen, depth + 1);
    return obj;
  }
  const record = obj as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    const value = record[key];
    if (SENSITIVE_KEY.test(key)) {
      if (value != null && value !== "") record[key] = REDACTED;
      continue;
    }
    if (value && typeof value === "object") redactInPlace(value, seen, depth + 1);
  }
  return obj;
}

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const environment =
  (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
  (import.meta.env.MODE as string | undefined) ||
  "production";
const release = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;

let initialized = false;

if (dsn) {
  try {
    Sentry.init({
      dsn,
      environment,
      release,
      // No performance spans from the browser yet — Tempo owns end-to-end tracing.
      tracesSampleRate: 0,
      sendDefaultPii: true,
      beforeSend(event) {
        try {
          redactInPlace(event.request);
          redactInPlace(event.extra);
          redactInPlace(event.contexts);
          redactInPlace(event.tags);
        } catch {
          /* never swallow an error payload due to scrubbing bugs */
        }
        return event;
      },
      beforeBreadcrumb(breadcrumb) {
        if (breadcrumb.category === "console") return null;
        return breadcrumb;
      },
    });
    initialized = true;
  } catch (err) {
    // Sentry init failure must never block app boot.
    // eslint-disable-next-line no-console
    console.warn("[sentry] init failed", err);
  }
}

export const sentryEnabled = initialized;

/**
 * Tag the current Sentry scope with the X-Request-Id value that was just
 * stamped on an outgoing request. authFetch calls this so that any error
 * captured between now and the next fetch will carry the same correlation
 * id the server logs did — one click in Sentry jumps to the matching Loki
 * log line.
 *
 * No-op when Sentry isn't initialized.
 */
export function setCurrentRequestId(requestId: string | undefined): void {
  if (!initialized || !requestId) return;
  try {
    Sentry.getCurrentScope().setTag("request_id", requestId);
  } catch {
    /* tag-setting should never throw, but don't crash the request if it does */
  }
}

/**
 * Exported so callers that want to wrap a React subtree with an error boundary
 * (e.g. `<App />`) don't need to re-import from `@sentry/react` directly.
 */
export const ErrorBoundary = Sentry.ErrorBoundary;

export { Sentry };
